import * as React from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { secretAutofillProps } from '@/components/ui/no-autofill';

export interface SecretInputProps
  extends Omit<React.ComponentProps<'input'>, 'type' | 'autoComplete'> {
  /** Show the eye button that reveals the value. Defaults to true. */
  revealable?: boolean;
  /** Classes for the positioning wrapper; put width caps (e.g. max-w-prose) here so the eye button stays inside the field. */
  wrapperClassName?: string;
}

/**
 * Masked input for API keys, client secrets and tokens on settings and admin
 * screens. Browsers and password managers will not fill the user's own login
 * into it (see `secretAutofillProps`). Not for real login or sign-up forms.
 */
const SecretInput = React.forwardRef<HTMLInputElement, SecretInputProps>(
  ({ className, wrapperClassName, revealable = true, disabled, id, ...props }, ref) => {
    const { t } = useTranslation('common');
    const [revealed, setRevealed] = React.useState(false);

    return (
      <div className={cn('relative', wrapperClassName)}>
        <Input
          ref={ref}
          id={id}
          type={revealed ? 'text' : 'password'}
          disabled={disabled}
          spellCheck={false}
          autoCapitalize="off"
          autoCorrect="off"
          className={cn(revealable && 'pr-10', className)}
          {...props}
          {...secretAutofillProps}
        />
        {revealable && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="absolute right-0 top-0 h-full px-3"
            aria-label={revealed ? t('secretInput.hide') : t('secretInput.show')}
            aria-controls={id}
            disabled={disabled}
            onClick={() => setRevealed((r) => !r)}
          >
            {revealed ? (
              <EyeOff className="h-4 w-4" aria-hidden="true" />
            ) : (
              <Eye className="h-4 w-4" aria-hidden="true" />
            )}
          </Button>
        )}
      </div>
    );
  },
);
SecretInput.displayName = 'SecretInput';

export { SecretInput };
